import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import styles from './index.module.scss'
import { NOTIFICATIONS_PAGE_URI } from '../../constants/uri.constant'
import DotsVerticalIcon from '../../assets/icon/DotsVerticalIcon'
import BellIcon from '../../assets/icon/BellIcon'
import WorkspaceModal from '../Modal/components/WorkspaceModal'
import AddMemberModal from '../AddMemberModal'
import UserService from '../../services/user.service'
import DropDown from '../DropDown'
import {TokenUtils, useUser} from 'cor-business'
import { getUserInitials } from '../../utils/userDisplay'
import StorageService from "../../services/storage.service";

interface HeaderProps {
    onWorkspaceCreated?: () => void
    onAvatarClick?: () => void
}

export default function Header({ onWorkspaceCreated, onAvatarClick }: HeaderProps) {
    const { user } = useUser()
    const navigate = useNavigate()
    const [workspaceModal, setWorkspaceModal] = useState(false)
    const [inviteModal, setInviteModal] = useState(false)
    const [avatar, setAvatar] = useState<string | null>(null)

    useEffect(() => {
        UserService.getPhoto()
            .then(setAvatar)
            .catch(() => {})
    }, [])

    const handleAvatarClick = () => {
        if (onAvatarClick) {
            onAvatarClick()
        }
    }

    const avatarInitials = getUserInitials(user, { profileFirst: true, emailFallback: 'U' })
    const userFullName = [
        user?.profile?.last_name || user?.last_name,
        user?.profile?.first_name || user?.first_name,
        user?.profile?.middle_name || user?.middle_name,
    ].filter(Boolean).join(' ') || user?.profile?.email || user?.email || 'Пользователь'
    // Только COR-ID (cor_id из /auth/v1/me) — внутренний UUID пользователю не показываем.
    const userCorId = user?.cor_id || user?.profile?.cor_id || ''

    const handleLogout = () => {
        TokenUtils.logout()
    }

    return (
        <>
            <div className={styles.header}>
                <div className={styles.header__left}>
                    <button className={styles.header__workspaceBtn} onClick={() => setWorkspaceModal(true)}>
                        <span className={styles.header__workspaceBtnIcon}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M7 0C7.55228 0 8 0.447715 8 1V13C8 13.5523 7.55228 14 7 14C6.44772 14 6 13.5523 6 13V1C6 0.447715 6.44772 0 7 0Z" fill="#453CFB" />
                                <path fillRule="evenodd" clipRule="evenodd" d="M0 7C0 6.44771 0.447715 6 1 6L13 6C13.5523 6 14 6.44772 14 7C14 7.55229 13.5523 8 13 8H1C0.447715 8 0 7.55228 0 7Z" fill="#453CFB" />
                            </svg>
                        </span>
                        <span>Пространство</span>
                    </button>
                    <button className={styles.header__inviteBtn} onClick={() => setInviteModal(true)}>
                        <span className={styles.header__inviteBtnIcon}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M7 0C7.55228 0 8 0.447715 8 1V13C8 13.5523 7.55228 14 7 14C6.44772 14 6 13.5523 6 13V1C6 0.447715 6.44772 0 7 0Z" fill="#F6F5F9" />
                                <path fillRule="evenodd" clipRule="evenodd" d="M0 7C0 6.44771 0.447715 6 1 6L13 6C13.5523 6 14 6.44772 14 7C14 7.55229 13.5523 8 13 8H1C0.447715 8 0 7.55228 0 7Z" fill="#F6F5F9" />
                            </svg>
                        </span>
                        <span>Участник</span>
                    </button>
                </div>
                <div className={styles.header__right}>
                    <div className={styles.header__icon}>
                        <DotsVerticalIcon />
                    </div>
                    <div className={styles.header__icon}>
                        <span className={styles.header__questionMark}>?</span>
                    </div>
                    <div
                        className={styles.header__icon}
                        onClick={() => navigate(NOTIFICATIONS_PAGE_URI)}
                        style={{ cursor: 'pointer' }}
                    >
                        <BellIcon />
                    </div>
                    <div className={styles.header__avatarDropdown}>
                        <DropDown
                            position="right"
                            btn={(
                                <div className={styles.header__avatar} onClick={handleAvatarClick} style={{ cursor: 'pointer' }}>
                                    {avatar ? (
                                        <img
                                            className={styles.header__avatarImg}
                                            src={avatar}
                                            alt="avatar"
                                            onError={() => setAvatar(null)}
                                        />
                                    ) : (
                                        <div className={styles.header__avatarInitials}>{avatarInitials}</div>
                                    )}
                                </div>
                            )}
                        >
                            <div className={styles.header__profileMenu}>
                                <div className={styles.header__profileCard}>
                                    <div className={styles.header__profileAvatar}>
                                        {avatar ? (
                                            <img
                                                className={styles.header__avatarImg}
                                                src={avatar}
                                                alt="avatar"
                                                onError={() => setAvatar(null)}
                                            />
                                        ) : (
                                            <div className={styles.header__avatarInitials}>{avatarInitials}</div>
                                        )}
                                    </div>
                                    <div className={styles.header__profileInfo}>
                                        <div className={styles.header__profileName}>{userFullName}</div>
                                        {userCorId && (
                                            <div className={styles.header__profileId}>COR-ID: {userCorId}</div>
                                        )}
                                    </div>
                                </div>
                                <button type="button" className={styles.header__logoutBtn} onClick={handleLogout}>
                                    <span>Выйти из аккаунта</span>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M10.3529 3.11042C10.7439 2.99314 11.1568 2.96888 11.5588 3.03957C11.9608 3.11026 12.3407 3.27395 12.6682 3.51757C12.9956 3.76118 13.2616 4.07798 13.4449 4.44267C13.6282 4.80736 13.7237 5.20985 13.7237 5.618V18.382C13.7237 18.7901 13.6282 19.1926 13.4449 19.5573C13.2616 19.922 12.9956 20.2388 12.6682 20.4824C12.3407 20.726 11.9608 20.8897 11.5588 20.9604C11.1568 21.0311 10.7439 21.0069 10.3529 20.8896L5.11607 19.3185C4.57676 19.1567 4.10397 18.8254 3.76782 18.3737C3.43168 17.922 3.25009 17.374 3.25 16.8109V7.18906C3.25009 6.62601 3.43168 6.07799 3.76782 5.62628C4.10397 5.17458 4.57676 4.84326 5.11607 4.68148L10.3529 3.11042ZM14.5965 5.01751C14.5965 4.78603 14.6885 4.56402 14.8522 4.40034C15.0159 4.23666 15.2379 4.1447 15.4694 4.1447H18.0878C18.7822 4.1447 19.4483 4.42057 19.9393 4.91162C20.4304 5.40267 20.7062 6.06868 20.7062 6.76313V7.63594C20.7062 7.86743 20.6143 8.08943 20.4506 8.25311C20.2869 8.4168 20.0649 8.50876 19.8334 8.50876C19.6019 8.50876 19.3799 8.4168 19.2162 8.25311C19.0526 8.08943 18.9606 7.86743 18.9606 7.63594V6.76313C18.9606 6.53165 18.8686 6.30965 18.705 6.14596C18.5413 5.98228 18.3193 5.89032 18.0878 5.89032H15.4694C15.2379 5.89032 15.0159 5.79836 14.8522 5.63468C14.6885 5.471 14.5965 5.24899 14.5965 5.01751ZM19.8334 15.4912C20.0649 15.4912 20.2869 15.5832 20.4506 15.7469C20.6143 15.9106 20.7062 16.1326 20.7062 16.3641V17.2369C20.7062 17.9313 20.4304 18.5973 19.9393 19.0884C19.4483 19.5794 18.7822 19.8553 18.0878 19.8553H15.4694C15.2379 19.8553 15.0159 19.7633 14.8522 19.5997C14.6885 19.436 14.5965 19.214 14.5965 18.9825C14.5965 18.751 14.6885 18.529 14.8522 18.3653C15.0159 18.2016 15.2379 18.1097 15.4694 18.1097H18.0878C18.3193 18.1097 18.5413 18.0177 18.705 17.854C18.8686 17.6904 18.9606 17.4684 18.9606 17.2369V16.3641C18.9606 16.1326 19.0526 15.9106 19.2162 15.7469C19.3799 15.5832 19.6019 15.4912 19.8334 15.4912ZM9.35968 11.1272C9.12819 11.1272 8.90619 11.2191 8.74251 11.3828C8.57882 11.5465 8.48687 11.7685 8.48687 12C8.48687 12.2315 8.57882 12.4535 8.74251 12.6172C8.90619 12.7809 9.12819 12.8728 9.35968 12.8728C9.59116 12.8728 9.81404 12.7809 9.97772 12.6172C10.1414 12.4535 10.2334 12.2315 10.2334 12C10.2334 11.7685 10.1414 11.5465 9.97772 11.3828C9.81404 11.2191 9.59116 11.1272 9.35968 11.1272Z" fill="currentColor" />
                                        <path fillRule="evenodd" clipRule="evenodd" d="M17.3811 9.54729C17.7716 9.15676 18.4047 9.15676 18.7953 9.54729L20.5409 11.2929C20.9314 11.6834 20.9314 12.3166 20.5409 12.7071L18.7953 14.4527C18.4047 14.8433 17.7716 14.8433 17.3811 14.4527C16.9905 14.0622 16.9905 13.4291 17.3811 13.0385L17.4196 13H15.4697C14.9174 13 14.4697 12.5523 14.4697 12C14.4697 11.4477 14.9174 11 15.4697 11H17.4196L17.3811 10.9615C16.9905 10.571 16.9905 9.93781 17.3811 9.54729Z" fill="currentColor" />
                                    </svg>
                                </button>
                            </div>
                        </DropDown>
                    </div>
                </div>
            </div>
            {workspaceModal && <WorkspaceModal onClose={() => setWorkspaceModal(false)} onCreated={onWorkspaceCreated} />}
            <AddMemberModal isOpen={inviteModal} onClose={() => setInviteModal(false)} />
        </>
    )
}
